import { CITIES } from '../data/cities';
import { AGENTS } from '../data/agents';
import { EXTRA_FILTER_DEFS } from '../utils/filterDefinitions';
import { FILTER_CODES } from '../utils/statusModel';

const API_BASE = '/api/public/v1';

// ---------- in-memory cache + inflight dedup ----------
const _CACHE_TTL = 60000;
const _CACHE_MAX = 50;
const _cache = new Map();
const _inflight = new Map();

function _cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > _CACHE_TTL) { _cache.delete(key); return null; }
  return entry.data;
}

function _cacheSet(key, data) {
  if (_cache.size >= _CACHE_MAX) _cache.delete(_cache.keys().next().value);
  _cache.set(key, { data, ts: Date.now() });
}

async function _fetchCached(url, signal) {
  const cached = _cacheGet(url);
  if (cached !== null) return cached;
  if (_inflight.has(url)) return _inflight.get(url);
  const p = (signal ? fetch(url, { signal }) : fetch(url))
    .then(res => { if (!res.ok) throw new Error(res.status); return res.json(); })
    .then(data => { if (!signal?.aborted) _cacheSet(url, data); _inflight.delete(url); return data; })
    .catch(err => { _inflight.delete(url); throw err; });
  _inflight.set(url, p);
  return p;
}
// ------------------------------------------------------

// Public API supports only a subset of filters; the rest are applied client-side.
const PROPERTY_TYPE_MATCHERS = {
  'single-family': ['single family', 'detached', 'residentialproperty'],
  townhouse: ['townhouse', 'att/row', 'row house'],
  condos: ['condo', 'condominium'],
  'co-ops': ['co-op', 'coop', 'co op'],
  'lots-land': ['land', 'lot', 'vacant'],
  'multi-family': ['multi-family', 'multi family', 'duplex', 'triplex', 'fourplex'],
  apartments: ['apartment', 'apt'],
};

function numParam(v) {
  const n = Number(v);
  return v !== '' && v != null && !Number.isNaN(n) ? n : null;
}

const SORT_MAP = {
  newest: 'date_desc',
  price_asc: 'price_asc',
  price_desc: 'price_desc',
  sqft: 'sqft_desc',
};

function buildStreetAddress(addr) {
  const parts = [
    addr.streetNumber,
    addr.streetDirectionPrefix || addr.streetDirection,
    addr.streetName,
    addr.streetSuffix,
  ].filter(Boolean);
  const street = parts.join(' ');
  const unit = addr.unitNumber ? ` #${addr.unitNumber}` : '';
  return street ? `${street}${unit}` : '';
}

export function normalizeListing(raw) {
  if (!raw) return raw;
  const price = raw.listPrice ?? raw.soldPrice ?? raw.price ?? 0;
  const isRental = raw.type === 'Lease';
  const status = raw.status === 'U' ? 'sold' : isRental ? 'for_rent' : 'for_sale';
  const sqftRaw = raw.details?.sqft ?? raw.sqft ?? 0;
  const sqft = sqftRaw != null ? (parseInt(sqftRaw, 10) || 0) : 0;
  const lotSizeRaw = raw.lot?.size ?? raw.lot?.squareFeet ?? raw.details?.landSquareFeet ?? raw.lotSize ?? null;
  const lotSize = lotSizeRaw != null ? (parseInt(lotSizeRaw, 10) || null) : null;
  const yearBuiltRaw = raw.details?.yearBuilt ?? raw.yearBuilt ?? null;
  const yearBuilt = yearBuiltRaw != null ? (parseInt(yearBuiltRaw, 10) || null) : null;
  const addr = raw.address;
  const addrIsObj = addr && typeof addr === 'object';
  const latRaw = raw.map?.latitude ?? raw.lat;
  const lngRaw = raw.map?.longitude ?? raw.lng;
  const lat = latRaw != null ? parseFloat(latRaw) : null;
  const lng = lngRaw != null ? parseFloat(lngRaw) : null;
  const photos = raw.photos ?? raw.images ?? [];

  const d = raw.details ?? {};
  const amenitySources = [d.extras, d.features, d.swimmingPool, d.airConditioning, d.heating, d.garage, d.balcony, d.style, d.waterfront];
  const searchableAmenities = amenitySources
    .filter((v) => typeof v === 'string' && v.trim())
    .join(', ')
    .toLowerCase()
    .split(/[,;/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const currentYear = new Date().getFullYear();
  const isNewConstruction = (yearBuilt != null && yearBuilt >= currentYear) || d.newConstructionYN === 'Yes';

  let address = '';
  if (addrIsObj) {
    if (addr.full) {
      address = addr.full;
    } else {
      const street = buildStreetAddress(addr);
      const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
      const zip = addr.zip || '';
      if (street) {
        address = [street, cityState, zip].filter(Boolean).join(', ');
      } else {
        address = cityState;
      }
    }
  } else {
    address = addr || '';
  }

  const hoaFee = parseFloat(
    raw.condominium?.fee ?? d.condoFees ?? d.maintenanceFee ?? ''
  ) || null;

  const streetAddress = addrIsObj
    ? buildStreetAddress(addr)
    : (address.split(',')[0]?.trim() || address);

  return {
    ...raw,
    id: raw.mlsNumber ?? raw.id,
    price,
    isRental,
    status,
    transactionType: raw.type ?? '',
    beds: d.numBedrooms ?? raw.beds ?? 0,
    baths: d.numBathrooms ?? raw.baths ?? 0,
    sqft,
    lotSize,
    yearBuilt,
    type: d.propertyType ?? '',
    class: raw.class ?? '',
    style: d.style ?? '',
    apiStatus: raw.status ?? '',
    lastStatus: raw.lastStatus ?? '',
    standardStatus: raw.standardStatus ?? '',
    numGarageSpaces: parseInt(d.numGarageSpaces, 10) || 0,
    subdivision: d.subdivision || (addrIsObj ? addr.subdivision : '') || '',
    isNewConstruction,
    searchableAmenities,
    address,
    streetAddress,
    city: raw.city || (addrIsObj ? addr.city : '') || '',
    state: raw.state || (addrIsObj ? addr.state : '') || '',
    zip: raw.zip || (addrIsObj ? addr.zip : '') || '',
    neighborhood: raw.neighborhood || (addrIsObj ? addr.neighborhood : '') || '',
    lat: lat != null && isFinite(lat) ? lat : null,
    lng: lng != null && isFinite(lng) ? lng : null,
    photos: photos.length ? photos : [],
    daysOnMarket: raw.daysOnMarket ?? null,
    listDate: raw.listDate ?? null,
    soldDate: raw.soldDate ?? null,
    mlsNumber: raw.mlsNumber ?? raw.id,
    rooms: raw.rooms ?? null,
    openHouses: raw.openHouses ?? [],
    history: raw.history ?? [],
    nearby: raw.nearby ?? {},
    tags: raw.tags ?? [],
    amenities: raw.amenities ?? [],
    priceHistory: (raw.history ?? []).map((h) => {
      const rawDate = h.soldDate ?? h.listDate ?? h.updatedOn ?? h.lastStatusUpdate ?? null;
      let date = rawDate;
      if (rawDate) {
        const dt = new Date(rawDate);
        if (!Number.isNaN(dt.getTime())) date = dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      return {
        date,
        price: h.soldPrice != null ? parseFloat(h.soldPrice) || null : (h.listPrice != null ? parseFloat(h.listPrice) || null : null),
        event: (h.soldPrice || h.soldDate) ? 'Sold' : (h.status === 'A' || h.lastStatus === 'New' || h.lastStatus === 'Lsd') ? 'Listed' : (h.lastStatus || h.status || ''),
      };
    }),
    schools: raw.nearby?.schools ?? [],
    pricePerSqft: price > 0 && sqft > 0 ? Math.round(price / sqft) : 0,
    // Extended detail fields
    description: d.description ?? '',
    heating: d.heating ?? '',
    cooling: d.airConditioning ?? '',
    parking: parseInt(d.numParkingSpaces, 10) || parseInt(d.numGarageSpaces, 10) || null,
    taxes: parseFloat(d.taxes) || null,
    taxesDate: d.taxesDate ?? null,
    virtualTourUrl: d.virtualTourUrl ?? null,
    hoa: hoaFee,
    numFireplaces: parseInt(d.numFireplaces, 10) || null,
    numBathroomsPlus: parseInt(d.numBathroomsPlus, 10) || null,
    numRooms: parseInt(d.numRooms, 10) || null,
    stories: parseInt(d.numStoreys, 10) || null,
    basement: d.basement ?? '',
    extras: d.extras ?? '',
    exterior: d.exterior ?? '',
    pool: d.pool ?? d.swimmingPool ?? '',
    waterfront: d.waterfront ?? d.waterFrontage ?? '',
    den: d.den ?? '',
    locker: d.locker ?? '',
    balcony: d.balcony ?? '',
    flooring: d.flooring ?? '',
    foundation: d.foundation ?? '',
    appliances: d.appliances ?? '',
    roofing: d.roofing ?? '',
    construction: d.construction ?? '',
    zoning: d.zoning ?? '',
    lotFront: parseFloat(d.lotFront) || null,
    lotDepth: parseFloat(d.lotDepth) || null,
    lotAcres: parseFloat(raw.lot?.acres) || null,
    garage: d.garage ?? '',
    landSquareFeet: parseInt(d.landSquareFeet, 10) || null,
    buildingAreaTotal: parseInt(d.buildingAreaTotal, 10) || null,
    lotSizeCode: d.lotSizeCode ?? '',
    sewage: d.sewage ?? '',
    sewer: d.sewer ?? '',
    water: d.water ?? '',
    waterSource: d.waterSource ?? '',
    utilities: d.utilities ?? '',
    electric: d.electric ?? '',
    pets: d.pets ?? '',
    petsAllowed: d.petsAllowed ?? '',
    petsComments: d.petsComments ?? '',
    leaseTimesPerYear: parseInt(d.leaseTimesPerYear, 10) || null,
    features: d.features ?? '',
    interiorFeatures: d.interiorFeatures ?? '',
    exteriorFeatures: d.exteriorFeatures ?? '',
    securityFeatures: d.securityFeatures ?? '',
    windowFeatures: d.windowFeatures ?? '',
    laundryFeatures: d.laundryFeatures ?? '',
    lotFeatures: d.lotFeatures ?? '',
    parkingFeatures: d.parkingFeatures ?? '',
    communityFeatures: d.communityFeatures ?? '',
    poolFeatures: d.poolFeatures ?? '',
    poolPrivateYN: d.poolPrivateYN ?? '',
    waterfrontFeatures: d.waterfrontFeatures ?? '',
    waterfrontYN: d.waterfrontYN ?? '',
    waterFrontage: d.waterFrontage ?? '',
    view: d.view ?? '',
    patioAndPorch: d.patioAndPorch ?? '',
    fencing: d.fencing ?? '',
    stormProtection: d.stormProtection ?? '',
    restrictions: d.restrictions ?? '',
    exteriorConstruction1: d.exteriorConstruction1 ?? '',
    roofMaterial: d.roofMaterial ?? '',
    newConstructionYN: d.newConstructionYN ?? '',
    architecturalStyle: d.architecturalStyle ?? '',
    propertyCondition: d.propertyCondition ?? '',
    directionFaces: d.directionFaces ?? '',
    roadFrontageType: d.roadFrontageType ?? '',
    roadSurfaceType: d.roadSurfaceType ?? '',
    entryLocation: d.entryLocation ?? '',
    possession: d.possession ?? '',
    disclosures: d.disclosures ?? '',
    directions: d.directions ?? '',
    subdivisionName: d.subdivisionName ?? '',
    complexName: d.complexName ?? '',
    developmentName: d.developmentName ?? '',
    seniorCommunity: d.seniorCommunity ?? '',
    elementarySchool: d.elementarySchool ?? '',
    middleOrJuniorSchool: d.middleOrJuniorSchool ?? '',
    highSchool: d.highSchool ?? '',
    associationFeeFrequency: d.associationFeeFrequency ?? '',
    associationPhone: d.associationPhone ?? '',
    associationFeeIncludes: d.associationFeeIncludes ?? '',
    associationAmenities: d.associationAmenities ?? '',
    membershipFee: parseFloat(d.membershipFee) || null,
    membershipRequired: d.membershipRequired ?? '',
    membershipDescription: d.membershipDescription ?? '',
    additionalFee: parseFloat(d.additionalFee) || null,
    previousListPrice: parseFloat(d.previousListPrice) || null,
    cumulativeDaysOnMarket: parseInt(d.cumulativeDaysOnMarket, 10) || null,
    taxLegalDescription: d.taxLegalDescription ?? '',
    taxAuthority: d.taxAuthority ?? '',
    parcelNumber: d.parcelNumber ?? '',
    originalPrice: raw.originalPrice != null ? parseFloat(raw.originalPrice) || null : null,
    soldPrice: raw.soldPrice != null ? parseFloat(raw.soldPrice) || null : null,
    condominium: raw.condominium ?? null,
    taxesAnnual: raw.taxes?.annualAmount != null ? parseFloat(raw.taxes.annualAmount) || null : null,
    assessmentYear: raw.taxes?.assessmentYear ?? null,
    agentName: raw.listingAgentName || '',
    officeName: raw.listingOfficeName || '',
    source: raw.boardId || '',
    updatedOn: raw.updatedOn || '',
  };
}

function buildListingParams(filters, sort, page, pageSize) {
  const params = new URLSearchParams();
  const statuses = Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : []);
  const bases = [...new Set(statuses.map((s) => {
    const fc = FILTER_CODES[s];
    return fc ? fc.base : String(s).split(':')[0];
  }))];
  if (bases.length === 1) params.set('status', bases[0]);
  else if (bases.length === 0) params.set('status', 'A');
  // mixed bases (A + U): omit status to fetch both, refine client-side.

  const listingType = Array.isArray(filters.listingType) ? filters.listingType : [];
  if (listingType.length === 1) {
    if (listingType[0] === 'Sale') params.set('type', 'Sale');
    else if (listingType[0] === 'Rent') params.set('type', 'lease');
  }

  if (filters.city) params.set('city', filters.city);
  if (filters.zip) params.set('zip', filters.zip);
  if (numParam(filters.minPrice) != null) params.set('minPrice', numParam(filters.minPrice));
  if (numParam(filters.maxPrice) != null) params.set('maxPrice', numParam(filters.maxPrice));
  if (numParam(filters.minBeds) != null) params.set('minBeds', numParam(filters.minBeds));
  if (numParam(filters.minBaths) != null) params.set('minBaths', numParam(filters.minBaths));
  if (numParam(filters.minSqft) != null) params.set('minSqft', numParam(filters.minSqft));
  if (numParam(filters.maxSqft) != null) params.set('maxSqft', numParam(filters.maxSqft));
  if (numParam(filters.minYearBuilt) != null) params.set('minYearBuilt', numParam(filters.minYearBuilt));
  // Extra fields: forwarded as params too (harmless if the API ignores them, works once supported server-side).
  EXTRA_FILTER_DEFS.forEach((def) => {
    if (def.keys) {
      def.keys.forEach((k) => { if (numParam(filters[k]) != null) params.set(k, numParam(filters[k])); });
    } else {
      const v = filters[def.key];
      if (Array.isArray(v)) { if (v.length) params.set(def.key, v.join(',')); }
      else if (v !== '' && v != null) params.set(def.key, v);
    }
  });
  if (filters.polygon) params.set('polygon', JSON.stringify([filters.polygon]));
  if (sort) params.set('sort', SORT_MAP[sort] || 'date_desc');
  if (page) params.set('page', page);
  if (pageSize) params.set('per_page', pageSize);
  return params;
}

// Client-side filtering for fields the public API does not filter on.
function applyClientFilters(items, filters = {}) {
  let out = items;
  const n = (v) => { const x = Number(v); return v !== '' && v != null && !Number.isNaN(x) ? x : null; };

  const statuses = Array.isArray(filters.status) ? filters.status : [];
  if (statuses.length) {
    out = out.filter((l) => statuses.some((tok) => {
      const fc = FILTER_CODES[tok];
      if (!fc) return l.lastStatus === tok;
      if (fc.baseMatch) return l.apiStatus === fc.base;
      return fc.codes.includes(l.lastStatus);
    }));
  }

  const listingType = Array.isArray(filters.listingType) ? filters.listingType : [];
  if (listingType.length) out = out.filter((l) => listingType.some((t) => (t === 'Rent' ? l.isRental : !l.isRental)));

  const propertyType = Array.isArray(filters.propertyType) ? filters.propertyType : [];
  if (propertyType.length) {
    out = out.filter((l) => {
      const hay = `${l.type} ${l.class} ${l.style}`.toLowerCase();
      return propertyType.some((slug) => (PROPERTY_TYPE_MATCHERS[slug] || [slug]).some((m) => hay.includes(m)));
    });
  }

  const maxYear = n(filters.maxYearBuilt); if (maxYear != null) out = out.filter((l) => l.yearBuilt != null && l.yearBuilt <= maxYear);
  const minDom = n(filters.minDom); if (minDom != null) out = out.filter((l) => (l.daysOnMarket ?? -1) >= minDom);
  const maxDom = n(filters.maxDom); if (maxDom != null) out = out.filter((l) => l.daysOnMarket != null && l.daysOnMarket <= maxDom);
  const minGarage = n(filters.minGarage); if (minGarage != null) out = out.filter((l) => (l.numGarageSpaces || 0) >= minGarage);

  const nb = (filters.neighborhood || '').trim().toLowerCase(); if (nb) out = out.filter((l) => (l.neighborhood || '').toLowerCase().includes(nb));
  const sub = (filters.subdivision || '').trim().toLowerCase(); if (sub) out = out.filter((l) => (l.subdivision || '').toLowerCase().includes(sub));

  // Extra multicheck fields that have raw data in the listing payload.
  const HAYSTACKS = {
    cooling: (l) => l.details?.airConditioning,
    heating: (l) => l.details?.heating,
    architecturalStyle: (l) => l.style || l.details?.style,
    appliances: (l) => l.details?.extras,
    interiorFeatures: (l) => l.details?.extras,
    exteriorFeatures: (l) => [l.details?.balcony, l.details?.extras].filter(Boolean).join(' '),
    poolFeatures: (l) => l.details?.swimmingPool,
    waterfrontFeatures: (l) => l.details?.waterfront,
    parkingFeatures: (l) => l.details?.garage,
  };
  Object.entries(HAYSTACKS).forEach(([key, get]) => {
    const sel = Array.isArray(filters[key]) ? filters[key] : [];
    if (sel.length) out = out.filter((l) => { const h = String(get(l) || '').toLowerCase(); return sel.some((v) => h.includes(String(v).toLowerCase())); });
  });

  // Extra boolean fields backed by payload data.
  if (filters.waterfront === 'true') out = out.filter((l) => !!l.details?.waterfront);
  if (filters.waterfront === 'false') out = out.filter((l) => !l.details?.waterfront);
  if (Array.isArray(filters.hasPool) && filters.hasPool.includes('true')) out = out.filter((l) => !!l.details?.swimmingPool);
  return out;
}

export async function getListings({ filters = {}, sort = 'newest', page = 1, pageSize = 12 } = {}) {
  const params = buildListingParams(filters, sort, page, pageSize);
  const res = await fetch(`${API_BASE}/listings/?${params}`);
  if (!res.ok) throw new Error(String(res.status));
  const data = await res.json();
  const items = applyClientFilters((data.results || []).map(normalizeListing), filters);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return { items, total, page, pageSize, totalPages };
}

export async function getMapMarkers({ filters = {}, bounds = null, limit = 300, signal } = {}) {
  const params = buildListingParams(filters, null, null, limit);
  if (bounds && !filters.polygon) {
    const { north, south, east, west } = bounds;
    if (north != null) params.set('n', north);
    if (south != null) params.set('s', south);
    if (east != null) params.set('e', east);
    if (west != null) params.set('w', west);
  }
  const url = `${API_BASE}/listings/?${params}`;
  try {
    const data = await _fetchCached(url, signal);
    return applyClientFilters((data.results || []).map(normalizeListing), filters);
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    console.error('getMapMarkers error', e);
    return [];
  }
}

export async function getAllListings({ filters = {}, sort = 'newest', page = 1, pageSize = 50, signal } = {}) {
  const params = buildListingParams(filters, sort, page, pageSize);
  if (!filters.polygon && filters.bounds) {
    const { north, south, east, west } = filters.bounds;
    if (north != null) params.set('n', north);
    if (south != null) params.set('s', south);
    if (east != null) params.set('e', east);
    if (west != null) params.set('w', west);
  }
  const url = `${API_BASE}/listings/?${params}`;
  const data = await _fetchCached(url, signal);
  const items = applyClientFilters((data.results || []).map(normalizeListing), filters);
  const total = data.count ?? items.length;
  const numPages = data.num_pages ?? 1;
  return { items, total, page, numPages };
}

export async function lookupListingByAddress(lat, lng, delta = 0.0009) {
  try {
    const params = new URLSearchParams({
      n: lat + delta, s: lat - delta,
      e: lng + delta, w: lng - delta,
      per_page: 50, status: 'A,U',
    });
    const res = await fetch(`${API_BASE}/listings/?${params}`);
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    const items = (data.results || [])
      .map(normalizeListing)
      .filter((l) => l.lat != null && l.lng != null);
    if (!items.length) return null;
    const sqDist = (l) => (l.lat - lat) ** 2 + (l.lng - lng) ** 2;
    const minD = Math.min(...items.map(sqDist));
    const nearest = items.filter((l) => sqDist(l) <= minD + 1e-9);
    const recency = (l) => Math.max(Date.parse(l.listDate) || 0, Date.parse(l.soldDate) || 0);
    nearest.sort((a, b) => recency(b) - recency(a));
    return nearest[0] || null;
  } catch (_) {
    return null;
  }
}

export async function getListingById(mls) {
  const res = await fetch(`${API_BASE}/listings/${mls}/`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(String(res.status));
  return normalizeListing(await res.json());
}

export async function getListingsByIds(ids = []) {
  const results = await Promise.all(ids.map((id) => getListingById(id).catch(() => null)));
  return results.filter(Boolean);
}

export async function getFeaturedListings(limit = 8) {
  const url = `${API_BASE}/listings/?${new URLSearchParams({ per_page: limit, status: 'A' })}`;
  try {
    const data = await _fetchCached(url);
    return (data.results || []).map(normalizeListing);
  } catch (e) {
    console.error('getFeaturedListings error', e);
    return [];
  }
}

export async function getSimilarListings(listing, limit = 4) {
  if (!listing) return [];
  try {
    const city = listing.city || listing.address?.city || '';
    const params = new URLSearchParams({ city, per_page: limit + 1 });
    const res = await fetch(`${API_BASE}/listings/?${params}`);
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    return (data.results || []).map(normalizeListing).filter((l) => l.id !== listing.id).slice(0, limit);
  } catch (e) {
    console.error('getSimilarListings error', e);
    return [];
  }
}

export async function getListingClusters(params = {}, signal) {
  const url = `${API_BASE}/listings/clusters/?${new URLSearchParams(params)}`;
  try {
    const data = await _fetchCached(url, signal);
    return data;
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    console.error('getListingClusters error', e);
    return [];
  }
}

export async function getListingStats(params = {}) {
  try {
    const qs = new URLSearchParams(params);
    const res = await fetch(`${API_BASE}/listings/stats/?${qs}`);
    if (!res.ok) throw new Error(res.status);
    return await res.json();
  } catch (e) {
    console.error('getListingStats error', e);
    return null;
  }
}

export async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(`${API_BASE}/geo/geocode/?q=${lat},${lng}`);
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data.results || []);
    return list[0] || null;
  } catch (e) {
    console.error('reverseGeocode error', e);
    return null;
  }
}

export async function getGeoBoundary(params = {}) {
  const qs = new URLSearchParams(params).toString();
  try {
    const res = await fetch(`${API_BASE}/geo/boundary/?${qs}`);
    if (res.ok) return await res.json();
    console.warn('getGeoBoundary public failed', res.status, qs);
  } catch (e) {
    console.warn('getGeoBoundary public error', e);
  }
  try {
    const res = await fetch(`/api/v1/geo/boundary/?${qs}`);
    if (res.ok) return await res.json();
    console.warn('getGeoBoundary panel failed', res.status, qs);
  } catch (e) {
    console.warn('getGeoBoundary panel error', e);
  }
  return null;
}

export async function getFilterMeta() {
  try {
    const res = await fetch(`${API_BASE}/meta/filters/`);
    if (!res.ok) throw new Error(res.status);
    return await res.json();
  } catch (e) {
    console.error('getFilterMeta error', e);
    return null;
  }
}

export async function getCities() {
  return Promise.all(
    CITIES.map(async (c) => {
      const stats = await getListingStats({ city: c.name, status: 'A' });
      return { ...c, count: stats?.total_count ?? 0 };
    })
  );
}

export async function getAgents() {
  return AGENTS;
}

export function getAgentById(id) {
  return AGENTS.find((a) => a.id === id) || AGENTS[0];
}

export async function getPublicAgents({ q = '', city = '', page = 1, perPage = 24 } = {}) {
  const params = new URLSearchParams({ page, per_page: perPage });
  if (q) params.set('q', q);
  if (city) params.set('city', city);
  try {
    const res = await fetch(`${API_BASE}/agents/?${params}`);
    if (!res.ok) throw new Error(res.status);
    return await res.json();
  } catch (e) {
    console.error('getPublicAgents error', e);
    return { count: 0, results: [], num_pages: 1, page: 1 };
  }
}

export async function getPublicAgentByRef(ref) {
  try {
    const res = await fetch(`${API_BASE}/agents/${ref}/`);
    if (!res.ok) throw new Error(res.status);
    return await res.json();
  } catch (e) {
    console.error('getPublicAgentByRef error', e);
    return null;
  }
}

export async function getLocationSuggestions(query = '') {
  try {
    const res = await fetch(`${API_BASE}/geo/autocomplete/?q=${encodeURIComponent(query)}`);
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    if (Array.isArray(data)) return data;
    if (data.results && Array.isArray(data.results)) return data.results;
    return [];
  } catch (e) {
    const needle = query.toLowerCase().trim();
    return CITIES.filter(
      (c) => !needle || c.name.toLowerCase().includes(needle) || c.state.toLowerCase().includes(needle)
    ).map((c) => ({ type: 'city', label: `${c.name}, ${c.state}`, value: c.id }));
  }
}

const SLOT_PLACEMENT_MAP = {
  'home-banner-a': 'header-leaderboard',
  'listings-sidebar-c': 'detail-sidebar-1',
  'grid-native': 'split-feed',
  'detail-sidebar-d': 'detail-sidebar-2',
  'detail-content-b': 'detail-sidebar-3',
};

export async function getAdBySlot(slotId) {
  const placementKey = SLOT_PLACEMENT_MAP[slotId];
  if (!placementKey) return null;
  try {
    const res = await fetch(`${API_BASE}/ads/serve/${placementKey}/`);
    if (!res.ok) return null;
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return null;
    const data = await res.json();
    if (!data) return null;
    const size = {
      width: data.width || 300,
      height: data.height || 250,
      applyUrl: data.apply_url || '/advertise',
    };
    if (data.kind === 'adsense' && data.adsense?.slot) {
      return { adsense: data.adsense, ...size };
    }
    if (data.kind === 'image' && data.image_url) {
      return {
        campaignId: data.campaign_id,
        imageUrl: data.image_url,
        clickUrl: data.click_url,
        ...size,
      };
    }
    if ((data.kind === 'campaign_script' || data.kind === 'placement_script') && data.script_code) {
      return { scriptHtml: data.script_code, kind: data.kind, ...size };
    }
    return { empty: true, ...size };
  } catch {
    return null;
  }
}

export async function createLead(payload) {
  const res = await fetch(`${API_BASE}/leads/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(res.status);
  return res.json();
}

export const STATS = {
  listings: 0,
  agents: AGENTS.length,
  cities: CITIES.length,
};
